// features/super-admin/pages/TenantsPage.tsx
/**
 * SUPER ADMIN — TENANT MANAGEMENT
 * Premium enterprise-grade tenant management with powerful filtering and actions.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
    Building2, Search, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, CheckCircle2, XCircle, Eye,
    Ban, RefreshCw, TrendingUp, TrendingDown, DollarSign, Users,
    Store, Calendar, ArrowUpDown, LayoutGrid, List, Zap, Activity,
    Clock, Shield, Layers, ArrowUp, ArrowDown, MoreHorizontal,
    Mail, Phone, Globe, Filter, X, Star, Award, Crown, Sparkles,
    Copy, ExternalLink, UserCheck, UserX, BarChart3,
    Plus, MessageSquare, Download, Key, Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface Tenant {
    id: string;
    name: string;
    slug: string;
    businessType: string;
    email: string | null;
    phone: string | null;
    isActive: boolean;
    businessModel?: 'single' | 'multi';
    isSubscribed?: boolean;
    subscriptionExpiresAt?: string | null;
    subscriptionTier: string;
    subscriptionStatus: string;
    trialEndsAt: string | null;
    businessesCount: number;
    branchesCount: number;
    staffCount: number;
    totalRevenue: string;
    createdAt: string;
    lastActivityAt: string | null;
}

interface TenantsResponse {
    tenants: Tenant[];
    totalCount: number;
    summary: {
        totalTenants: number;
        activeTenants: number;
        trialTenants: number;
        totalRevenue: string;
    };
}

// ============================================================
// CONSTANTS
// ============================================================

const TIER_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
    enterprise: { icon: Crown, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Enterprise' },
    business: { icon: Building2, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200', label: 'Business' },
    starter: { icon: Zap, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Starter' },
    free: { icon: Star, color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200', label: 'Free' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0';
    if (num >= 1_000_000) return `₦${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `₦${(num / 1_000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG')}`;
};

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

// ============================================================
// GRADIENT COLORS FOR TENANT AVATARS
// ============================================================

const AVATAR_GRADIENTS = [
    'from-violet-500 to-purple-600',
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-rose-500 to-pink-600',
    'from-cyan-500 to-sky-600',
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function TenantsPage() {
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [tierFilter, setTierFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [confirmAction, setConfirmAction] = useState<{ type: string; tenant: Tenant } | null>(null);
    const [showDetailPanel, setShowDetailPanel] = useState(false);
    const limit = 12;

    const [showProvisionModal, setShowProvisionModal] = useState(false);
    const [provisionForm, setProvisionForm] = useState({
        companyName: '',
        companySlug: '',
        businessType: 'retail',
        businessModel: 'single' as 'single' | 'multi',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPhone: '',
        adminPassword: '',
        subscriptionTier: 'starter' as 'starter' | 'business' | 'enterprise',
        durationMonths: 12,
    });
    const [createdCredentials, setCreatedCredentials] = useState<{ email: string; pass: string; org: string } | null>(null);

    // ==================== QUERIES ====================

    const { data, isLoading, isError, refetch } = useQuery<TenantsResponse>({
        queryKey: ['super-admin-tenants', page, search, statusFilter, tierFilter, sortBy, order],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/tenants', {
                params: { limit, page, search, status: statusFilter || undefined, sortBy, order },
            });
            return data?.data;
        },
        enabled: !!user,
    });

    // ==================== MUTATIONS ====================

    const toggleTenantMutation = useMutation({
        mutationFn: ({ tenantId, activate }: { tenantId: string; activate: boolean }) =>
            activate
                ? apiClient.post(`/super-admin/tenants/${tenantId}/activate`)
                : apiClient.post(`/super-admin/tenants/${tenantId}/deactivate`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            addToast({ type: 'success', title: 'Tenant Updated' });
            setConfirmAction(null);
            setSelectedTenant(null);
            setShowDetailPanel(false);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const provisionMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post('/super-admin/tenants', payload),
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            addToast({
                type: 'success',
                title: 'Organization Provisioned',
                message: `${provisionForm.companyName} is ready and fully active!`,
            });
            setCreatedCredentials({
                email: provisionForm.adminEmail,
                pass: res?.data?.data?.initialPassword || provisionForm.adminPassword,
                org: provisionForm.companyName,
            });
            setShowProvisionModal(false);
            setProvisionForm({
                companyName: '',
                companySlug: '',
                businessType: 'retail',
                businessModel: 'single',
                adminFirstName: '',
                adminLastName: '',
                adminEmail: '',
                adminPhone: '',
                adminPassword: '',
                subscriptionTier: 'starter',
                durationMonths: 12,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Provisioning Failed',
                message: err?.response?.data?.message || err?.message || 'Could not provision organization',
            });
        },
    });

    const impersonateMutation = useMutation({
        mutationFn: (tenantId: string) => apiClient.post(`/super-admin/tenants/${tenantId}/impersonate`),
        onSuccess: (res: any) => {
            const data = res?.data?.data;
            if (data?.tokens?.accessToken) {
                const currentAccess = localStorage.getItem('accessToken');
                const currentRefresh = localStorage.getItem('refreshToken');
                if (currentAccess) localStorage.setItem('originalPlatformAccessToken', currentAccess);
                if (currentRefresh) localStorage.setItem('originalPlatformRefreshToken', currentRefresh);
                localStorage.setItem('impersonatedTenantName', data.tenant.name);
                localStorage.setItem('accessToken', data.tokens.accessToken);
                localStorage.setItem('refreshToken', data.tokens.refreshToken);
                localStorage.setItem('tenantId', data.tenant.id);
                localStorage.setItem('tenantSlug', data.tenant.slug);
                if (data.selectedBusinessId) localStorage.setItem('selectedBusinessId', data.selectedBusinessId);
                if (data.selectedBranchId) localStorage.setItem('selectedBranchId', data.selectedBranchId);
                addToast({
                    type: 'success',
                    title: 'Entered Support Mode',
                    message: `Now viewing as ${data.tenant.name}`,
                });
                window.location.href = '/dashboard';
            }
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Impersonation Failed',
                message: err?.response?.data?.message || 'Could not access tenant store',
            });
        },
    });

    const handleExportCsv = () => {
        if (!tenants || tenants.length === 0) {
            addToast({ type: 'warning', title: 'No Data', message: 'No organizations to export' });
            return;
        }
        const headers = ['Name', 'Slug', 'Status', 'Tier', 'Subscribed', 'Business Model', 'Email', 'Phone', 'Businesses', 'Staff', 'Joined'];
        const rows = tenants.map(t => [
            t.name,
            t.slug,
            t.isActive ? 'Active' : 'Inactive',
            t.subscriptionTier,
            t.isSubscribed ? 'Yes' : 'No',
            t.businessModel || 'single',
            t.email || '',
            t.phone || '',
            t.businessesCount || 0,
            t.staffCount || 0,
            formatDate(t.createdAt),
        ]);
        const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `etipos-tenants-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        addToast({ type: 'success', title: 'Export Complete', message: 'Tenants CSV downloaded successfully' });
    };

    // ==================== DERIVED DATA ====================

    const tenants = data?.tenants || [];
    const summary = data?.summary;
    const totalCount = data?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const hasActiveFilters = !!(search || statusFilter || tierFilter);

    const clearFilters = () => {
        setSearch('');
        setStatusFilter('');
        setTierFilter('');
        setPage(1);
    };

    // ==================== LOADING / ERROR ====================

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="text-center">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 rounded-2xl border-4 border-violet-200 border-t-violet-600 mx-auto mb-6" />
                <p className="text-slate-500 font-medium">Loading tenants...</p>
            </div>
        </div>
    );

    if (isError) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Failed to load tenants" onRetry={() => refetch()} />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* ========== HERO HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-gradient-to-br from-violet-700 via-purple-800 to-indigo-900 rounded-3xl shadow-2xl">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    </div>
                    <div className="relative px-8 lg:px-12 py-8 lg:py-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div whileHover={{ scale: 1.05 }}
                                    className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shadow-2xl">
                                    <Building2 size={32} className="text-white" />
                                </motion.div>
                                <div>
                                    <p className="text-violet-200 text-sm font-medium">Super Admin</p>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white">Tenant Management</h1>
                                    <p className="text-violet-200 text-sm mt-1">{totalCount.toLocaleString()} organizations registered on the platform</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button onClick={handleExportCsv}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl hover:bg-white/20 transition-all text-sm font-medium">
                                    <Download size={14} /> Export CSV
                                </button>
                                <button onClick={() => refetch()}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl hover:bg-white/20 transition-all text-sm font-medium">
                                    <RefreshCw size={14} /> Refresh
                                </button>
                                <button onClick={() => setShowProvisionModal(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-violet-900 rounded-xl hover:bg-violet-50 transition-all text-sm font-bold shadow-xl">
                                    <Plus size={16} /> Provision Organization
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== SUMMARY METRICS ========== */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Total Tenants', value: summary.totalTenants.toLocaleString(), icon: Building2, gradient: 'from-violet-500 to-purple-600', trend: 'up', trendValue: `${((summary.activeTenants / Math.max(summary.totalTenants, 1)) * 100).toFixed(0)}% active` },
                            { label: 'Active', value: summary.activeTenants.toLocaleString(), icon: CheckCircle2, gradient: 'from-emerald-500 to-teal-600' },
                            { label: 'On Trial', value: summary.trialTenants.toLocaleString(), icon: Clock, gradient: 'from-amber-500 to-orange-600' },
                            { label: 'Total Revenue', value: formatNaira(summary.totalRevenue), icon: DollarSign, gradient: 'from-blue-500 to-indigo-600' },
                        ].map((stat, i) => (
                            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
                                <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                    <stat.icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                                {stat.trend && (
                                    <p className="text-[10px] text-emerald-500 mt-0.5 flex items-center gap-1">
                                        <ArrowUp size={10} />{stat.trendValue}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* ========== FILTERS BAR ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                    <div className="flex flex-col lg:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text" placeholder="Search by name, slug, or email..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 focus:bg-white transition-all"
                            />
                            {search && (
                                <button onClick={() => { setSearch(''); setPage(1); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Status Filter */}
                            <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                                {['', 'active', 'inactive', 'trial'].map(s => (
                                    <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                                        className={clsx('px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize',
                                            statusFilter === s ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                                        {s || 'All'}
                                    </button>
                                ))}
                            </div>
                            {/* Tier Filter */}
                            <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                                {['', 'enterprise', 'business', 'starter', 'free'].map(t => (
                                    <button key={t} onClick={() => { setTierFilter(t); setPage(1); }}
                                        className={clsx('px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize',
                                            tierFilter === t ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                                        {t || 'All Tiers'}
                                    </button>
                                ))}
                            </div>
                            {/* View Toggle */}
                            <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                                <button onClick={() => setViewMode('grid')}
                                    className={clsx('p-2 rounded-lg transition-all', viewMode === 'grid' ? 'bg-white shadow-sm text-violet-600' : 'text-slate-400')}>
                                    <LayoutGrid size={16} />
                                </button>
                                <button onClick={() => setViewMode('table')}
                                    className={clsx('p-2 rounded-lg transition-all', viewMode === 'table' ? 'bg-white shadow-sm text-violet-600' : 'text-slate-400')}>
                                    <List size={16} />
                                </button>
                            </div>
                            {/* Sort */}
                            <select value={`${sortBy}-${order}`} onChange={e => { const [s, o] = e.target.value.split('-'); setSortBy(s); setOrder(o as 'asc' | 'desc'); }}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600">
                                <option value="createdAt-desc">Newest First</option>
                                <option value="createdAt-asc">Oldest First</option>
                                <option value="name-asc">Name A-Z</option>
                                <option value="name-desc">Name Z-A</option>
                            </select>
                            {hasActiveFilters && (
                                <button onClick={clearFilters}
                                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors">
                                    <X size={14} /> Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ========== TENANTS GRID ========== */}
                {tenants.length === 0 ? (
                    <EmptyState
                        icon={<Building2 size={48} className="text-slate-300" />}
                        title="No tenants found"
                        description={hasActiveFilters ? 'Try adjusting your filters.' : 'No organizations registered yet.'}
                        action={hasActiveFilters ? { label: 'Clear Filters', onClick: clearFilters } : undefined}
                    />
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {tenants.map((t, i) => {
                            const tierConfig = TIER_CONFIG[t.subscriptionTier] || TIER_CONFIG.free;
                            const TierIcon = tierConfig.icon;
                            const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                            const isSelected = selectedTenant?.id === t.id;

                            return (
                                <motion.div
                                    key={t.id}
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.03 }}
                                    whileHover={{ y: -6, scale: 1.02 }}
                                    className={clsx(
                                        'relative bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 cursor-pointer overflow-hidden group',
                                        isSelected ? 'border-violet-400 shadow-lg ring-2 ring-violet-500/20' : 'border-slate-200 hover:border-violet-300 hover:shadow-xl'
                                    )}
                                    onClick={() => {
                                        if (isSelected) {
                                            setSelectedTenant(null);
                                            setShowDetailPanel(false);
                                        } else {
                                            setSelectedTenant(t);
                                            setShowDetailPanel(true);
                                        }
                                    }}
                                >
                                    {/* Status indicator bar */}
                                    <div className={clsx('h-1.5', t.isActive ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-rose-500 to-pink-600')} />

                                    <div className="p-5">
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className={clsx('w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-white font-bold text-lg shadow-lg', gradient)}>
                                                {getInitials(t.name)}
                                            </div>
                                            <div className="flex flex-col gap-1 items-end">
                                                <Badge variant={t.isSubscribed ? 'success' : 'danger'} size="sm">
                                                    {t.isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                                                </Badge>
                                                <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', tierConfig.bg, tierConfig.color)}>
                                                    <TierIcon size={10} />{t.businessModel === 'single' ? 'Single' : 'Multi'} • {tierConfig.label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Name & Slug */}
                                        <h3 className="font-bold text-slate-900 text-base mb-1 truncate">{t.name}</h3>
                                        <code className="text-xs text-slate-400 font-mono block mb-3">@{t.slug}</code>

                                        {/* Stats Grid */}
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                            <div className="p-2 bg-slate-50 rounded-lg text-center">
                                                <p className="text-[10px] text-slate-400 uppercase">Businesses</p>
                                                <p className="text-sm font-bold text-slate-700">{t.businessesCount || 0}</p>
                                            </div>
                                            <div className="p-2 bg-slate-50 rounded-lg text-center">
                                                <p className="text-[10px] text-slate-400 uppercase">Staff</p>
                                                <p className="text-sm font-bold text-slate-700">{t.staffCount || 0}</p>
                                            </div>
                                        </div>

                                        {/* Contact */}
                                        <div className="space-y-1 mb-3">
                                            {t.email && (
                                                <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                                    <Mail size={10} />{t.email}
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                                <Calendar size={10} />Joined {formatDate(t.createdAt)}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1.5 pt-3 border-t border-slate-100">
                                            <button onClick={e => { e.stopPropagation(); navigate(`/super-admin/tenants/${t.id}`); }}
                                                className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors">
                                                <Eye size={12} /> View
                                            </button>
                                            <button onClick={e => { e.stopPropagation(); impersonateMutation.mutate(t.id); }}
                                                disabled={impersonateMutation.isPending}
                                                title="Access Store as Support"
                                                className="flex items-center justify-center gap-1 px-2.5 py-2 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors">
                                                <Shield size={12} /> Support
                                            </button>
                                            {t.phone && (
                                                <a href={`https://wa.me/${t.phone.replace(/[^0-9]/g, '')}`}
                                                    target="_blank" rel="noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    title="Chat on WhatsApp"
                                                    className="flex items-center justify-center p-2 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors">
                                                    <MessageSquare size={13} />
                                                </a>
                                            )}
                                            <button onClick={e => {
                                                e.stopPropagation();
                                                setConfirmAction({ type: t.isActive ? 'deactivate' : 'activate', tenant: t });
                                            }}
                                                title={t.isActive ? 'Deactivate' : 'Activate'}
                                                className={clsx(
                                                    'flex items-center justify-center p-2 text-xs font-medium rounded-lg border transition-colors',
                                                    t.isActive
                                                        ? 'text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100'
                                                        : 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                                                )}>
                                                {t.isActive ? <XCircle size={13} /> : <CheckCircle2 size={13} />}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    /* ========== TABLE VIEW ========== */
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase">Organization</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase hidden lg:table-cell">Plan</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Businesses</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Staff</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase hidden lg:table-cell">Revenue</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase hidden xl:table-cell">Joined</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {tenants.map((t, i) => {
                                        const tierConfig = TIER_CONFIG[t.subscriptionTier] || TIER_CONFIG.free;
                                        const TierIcon = tierConfig.icon;
                                        const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];

                                        return (
                                            <motion.tr key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                                                className="hover:bg-slate-50 group cursor-pointer"
                                                onClick={() => navigate(`/super-admin/tenants/${t.id}`)}>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={clsx('w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0', gradient)}>
                                                            {getInitials(t.name)}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900">{t.name}</p>
                                                            <code className="text-xs text-slate-400 font-mono">{t.slug}</code>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 hidden lg:table-cell">
                                                    <span className={clsx('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border', tierConfig.bg, tierConfig.color)}>
                                                        <TierIcon size={10} />{tierConfig.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center hidden md:table-cell text-sm font-medium">{t.businessesCount || 0}</td>
                                                <td className="px-6 py-4 text-center hidden md:table-cell text-sm font-medium">{t.staffCount || 0}</td>
                                                <td className="px-6 py-4 text-right hidden lg:table-cell text-sm font-bold text-emerald-600">{formatNaira(t.totalRevenue)}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge variant={t.isActive ? 'success' : 'danger'} size="sm">
                                                        {t.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 hidden xl:table-cell text-xs text-slate-400">{formatDate(t.createdAt)}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={e => { e.stopPropagation(); navigate(`/super-admin/tenants/${t.id}`); }}
                                                            className="p-2 rounded-lg hover:bg-violet-50 text-slate-400 hover:text-violet-600" title="View Details">
                                                            <Eye size={15} />
                                                        </button>
                                                        <button onClick={e => { e.stopPropagation(); impersonateMutation.mutate(t.id); }}
                                                            className="p-2 rounded-lg hover:bg-purple-50 text-slate-400 hover:text-purple-600" title="Access Store as Support">
                                                            <Shield size={15} />
                                                        </button>
                                                        {t.phone && (
                                                            <a href={`https://wa.me/${t.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                                className="p-2 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600" title="WhatsApp">
                                                                <MessageSquare size={15} />
                                                            </a>
                                                        )}
                                                        <button onClick={e => { e.stopPropagation(); setConfirmAction({ type: t.isActive ? 'deactivate' : 'activate', tenant: t }); }}
                                                            className={clsx('p-2 rounded-lg', t.isActive ? 'hover:bg-rose-50 text-slate-400 hover:text-rose-500' : 'hover:bg-emerald-50 text-slate-400 hover:text-emerald-500')}
                                                            title={t.isActive ? 'Deactivate' : 'Activate'}>
                                                            {t.isActive ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ========== PAGINATION ========== */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4">
                        <div className="flex items-center gap-4">
                            <p className="text-sm text-slate-500">
                                Page <span className="font-bold text-slate-700">{page}</span> of{' '}
                                <span className="font-bold text-slate-700">{totalPages}</span> ·{' '}
                                <span className="font-bold text-slate-700">{totalCount.toLocaleString()}</span> tenants
                            </p>
                            <select value={limit} onChange={e => { setPage(1); }}
                                className="px-2 py-1 border border-slate-200 rounded-lg text-xs text-slate-600">
                                <option value={12}>12 per page</option>
                                <option value={24}>24 per page</option>
                                <option value={48}>48 per page</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(1)} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-400"><ChevronsLeft size={16} /></button>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-400"><ChevronLeft size={16} /></button>
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                let pageNum: number;
                                if (totalPages <= 7) pageNum = i + 1;
                                else if (page <= 4) pageNum = i + 1;
                                else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                                else pageNum = page - 3 + i;
                                return (
                                    <button key={pageNum} onClick={() => setPage(pageNum)}
                                        className={clsx('w-9 h-9 rounded-lg text-sm font-medium transition-all',
                                            page === pageNum ? 'bg-violet-500 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100')}>
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-400"><ChevronRight size={16} /></button>
                            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-400"><ChevronsRight size={16} /></button>
                        </div>
                    </div>
                )}

                {/* ========== CONFIRMATION MODAL ========== */}
                <AnimatePresence>
                    {confirmAction && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setConfirmAction(null)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center border border-slate-200">
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
                                    className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${confirmAction.type === 'deactivate' ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                                    {confirmAction.type === 'deactivate' ? (
                                        <Ban size={28} className="text-rose-500" />
                                    ) : (
                                        <CheckCircle2 size={28} className="text-emerald-500" />
                                    )}
                                </motion.div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    {confirmAction.type === 'deactivate' ? 'Deactivate' : 'Activate'} {confirmAction.tenant.name}?
                                </h3>
                                <p className="text-sm text-slate-500 mt-2">
                                    {confirmAction.type === 'deactivate'
                                        ? 'This will suspend all access for this organization and its users.'
                                        : 'This will restore access for this organization and its users.'}
                                </p>
                                <div className="flex gap-3 mt-6">
                                    <button onClick={() => setConfirmAction(null)}
                                        className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition">
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => toggleTenantMutation.mutate({ tenantId: confirmAction.tenant.id, activate: confirmAction.type === 'activate' })}
                                        disabled={toggleTenantMutation.isPending}
                                        className={clsx(
                                            'flex-1 px-5 py-3 rounded-xl font-semibold text-sm shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-white',
                                            confirmAction.type === 'deactivate'
                                                ? 'bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600'
                                                : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600'
                                        )}>
                                        {toggleTenantMutation.isPending ? (
                                            <Spinner size="xs" variant="white" />
                                        ) : confirmAction.type === 'deactivate' ? (
                                            'Deactivate'
                                        ) : (
                                            'Activate'
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* ========== PROVISION ORGANIZATION MODAL ========== */}
                    {showProvisionModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowProvisionModal(false)}>
                            <motion.div initial={{ scale: 0.95, y: 15 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 15 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                                
                                {/* Modal Header */}
                                <div className="bg-gradient-to-r from-violet-600 via-purple-700 to-indigo-800 p-6 text-white sticky top-0 z-10 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                            <Sparkles size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold">Provision New Organization</h2>
                                            <p className="text-violet-200 text-xs">Direct platform onboarding with instant active subscription</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setShowProvisionModal(false)}
                                        className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Modal Body */}
                                <div className="p-6 space-y-6">
                                    {/* Section 1: Business Details */}
                                    <div>
                                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">1. Organization Details</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Company Name *</label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="e.g. Acme Supermarket"
                                                    value={provisionForm.companyName}
                                                    onChange={e => {
                                                        const name = e.target.value;
                                                        const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                                                        setProvisionForm(f => ({
                                                            ...f,
                                                            companyName: name,
                                                            companySlug: f.companySlug === '' || f.companySlug === autoSlug.slice(0, -1) ? autoSlug : f.companySlug,
                                                        }));
                                                    }}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Workspace Slug *</label>
                                                <div className="relative">
                                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-400">@</span>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="acme-supermarket"
                                                        value={provisionForm.companySlug}
                                                        onChange={e => setProvisionForm(f => ({ ...f, companySlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                                                        className="w-full pl-8 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white transition-all"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Business Model</label>
                                                <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
                                                    <button
                                                        type="button"
                                                        onClick={() => setProvisionForm(f => ({ ...f, businessModel: 'single' }))}
                                                        className={clsx('py-2 text-xs font-bold rounded-lg transition-all', provisionForm.businessModel === 'single' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500')}>
                                                        Single Store
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setProvisionForm(f => ({ ...f, businessModel: 'multi' }))}
                                                        className={clsx('py-2 text-xs font-bold rounded-lg transition-all', provisionForm.businessModel === 'multi' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500')}>
                                                        Multi-Branch
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Industry Category</label>
                                                <select
                                                    value={provisionForm.businessType}
                                                    onChange={e => setProvisionForm(f => ({ ...f, businessType: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                                                    <option value="retail">General Retail</option>
                                                    <option value="supermarket">Supermarket / Grocery</option>
                                                    <option value="restaurant">Restaurant & Bar</option>
                                                    <option value="pharmacy">Pharmacy & Health</option>
                                                    <option value="fashion">Fashion & Boutique</option>
                                                    <option value="electronics">Electronics & Gadgets</option>
                                                    <option value="services">Services & Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 2: Store Admin */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">2. Store Super Admin Account</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin First Name *</label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="John"
                                                    value={provisionForm.adminFirstName}
                                                    onChange={e => setProvisionForm(f => ({ ...f, adminFirstName: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin Last Name *</label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="Doe"
                                                    value={provisionForm.adminLastName}
                                                    onChange={e => setProvisionForm(f => ({ ...f, adminLastName: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin Email *</label>
                                                <input
                                                    type="email"
                                                    required
                                                    placeholder="owner@acme.com"
                                                    value={provisionForm.adminEmail}
                                                    onChange={e => setProvisionForm(f => ({ ...f, adminEmail: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin Phone (WhatsApp)</label>
                                                <input
                                                    type="tel"
                                                    placeholder="+234 801 234 5678"
                                                    value={provisionForm.adminPhone}
                                                    onChange={e => setProvisionForm(f => ({ ...f, adminPhone: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                                />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <label className="block text-xs font-semibold text-slate-700">Initial Password</label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
                                                            let p = 'Eti';
                                                            for (let i = 0; i < 7; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
                                                            setProvisionForm(f => ({ ...f, adminPassword: p }));
                                                        }}
                                                        className="text-xs font-semibold text-violet-600 hover:text-violet-700">
                                                        Auto-generate Secure Password
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Leave empty to auto-generate upon provisioning"
                                                    value={provisionForm.adminPassword}
                                                    onChange={e => setProvisionForm(f => ({ ...f, adminPassword: e.target.value }))}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 3: Subscription Plan & Duration */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">3. Immediate Subscription Grant</h3>
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            {[
                                                { id: 'starter', label: 'Starter', price: '₦25k/mo', desc: 'Single store' },
                                                { id: 'business', label: 'Business', price: '₦50k/mo', desc: 'Up to 3 branches' },
                                                { id: 'enterprise', label: 'Enterprise', price: '₦120k/mo', desc: 'Unlimited & BOM' },
                                            ].map(plan => (
                                                <button
                                                    key={plan.id}
                                                    type="button"
                                                    onClick={() => setProvisionForm(f => ({ ...f, subscriptionTier: plan.id as any }))}
                                                    className={clsx(
                                                        'p-3.5 rounded-2xl border-2 text-left transition-all',
                                                        provisionForm.subscriptionTier === plan.id
                                                            ? 'border-violet-600 bg-violet-50/50 ring-2 ring-violet-500/20'
                                                            : 'border-slate-200 hover:border-slate-300'
                                                    )}>
                                                    <p className="text-xs font-bold text-slate-900">{plan.label}</p>
                                                    <p className="text-sm font-extrabold text-violet-600 mt-0.5">{plan.price}</p>
                                                    <p className="text-[10px] text-slate-400 mt-1">{plan.desc}</p>
                                                </button>
                                            ))}
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Subscription Duration</label>
                                            <select
                                                value={provisionForm.durationMonths}
                                                onChange={e => setProvisionForm(f => ({ ...f, durationMonths: parseInt(e.target.value) }))}
                                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                                                <option value={1}>1 Month Access</option>
                                                <option value={3}>3 Months Access (Quarterly)</option>
                                                <option value={6}>6 Months Access (Bi-Annual)</option>
                                                <option value={12}>12 Months Access (1 Year Enterprise License)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Footer */}
                                <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 sticky bottom-0">
                                    <button
                                        type="button"
                                        onClick={() => setShowProvisionModal(false)}
                                        className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-100 transition-colors">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={provisionMutation.isPending || !provisionForm.companyName || !provisionForm.companySlug || !provisionForm.adminEmail}
                                        onClick={() => provisionMutation.mutate(provisionForm)}
                                        className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 via-purple-700 to-indigo-800 text-white font-bold text-sm shadow-xl shadow-violet-500/20 hover:opacity-95 transition-all disabled:opacity-50 flex items-center gap-2">
                                        {provisionMutation.isPending ? (
                                            <>
                                                <Spinner size="xs" variant="white" />
                                                <span>Provisioning Workspace...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Crown size={16} />
                                                <span>Provision & Activate Instantly</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* ========== CREDENTIALS DISPLAY MODAL ========== */}
                    {createdCredentials && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setCreatedCredentials(null)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-md p-8 text-center">
                                
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white mx-auto mb-4 shadow-xl shadow-emerald-500/20">
                                    <Check size={32} />
                                </div>

                                <h3 className="text-xl font-bold text-slate-900">Organization Ready!</h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    Workspace <span className="font-bold text-slate-700">{createdCredentials.org}</span> has been provisioned and activated.
                                </p>

                                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 my-6 text-left space-y-3">
                                    <div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Login URL</p>
                                        <p className="text-xs font-mono font-medium text-slate-700 mt-0.5">{window.location.origin}/login</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Admin Email</p>
                                        <p className="text-xs font-mono font-bold text-slate-900 mt-0.5">{createdCredentials.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Initial Password</p>
                                        <p className="text-sm font-mono font-black text-violet-700 mt-0.5 bg-violet-50 p-2 rounded-lg border border-violet-100 flex items-center justify-between">
                                            <span>{createdCredentials.pass}</span>
                                            <Key size={14} className="text-violet-400" />
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <button
                                        onClick={() => {
                                            const text = `EtiPOS Workspace Provisioned!\nStore: ${createdCredentials.org}\nLogin URL: ${window.location.origin}/login\nEmail: ${createdCredentials.email}\nPassword: ${createdCredentials.pass}`;
                                            navigator.clipboard.writeText(text);
                                            addToast({ type: 'success', title: 'Credentials Copied', message: 'Ready to share with store owner' });
                                        }}
                                        className="w-full py-3 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors">
                                        <Copy size={16} /> Copy Credentials
                                    </button>

                                    <button
                                        onClick={() => {
                                            const text = encodeURIComponent(
                                                `Hello! Your EtiPOS workspace has been provisioned.\n\nStore: ${createdCredentials.org}\nLogin URL: ${window.location.origin}/login\nEmail: ${createdCredentials.email}\nPassword: ${createdCredentials.pass}\n\nPlease log in and update your password upon first entry.`
                                            );
                                            window.open(`https://wa.me/?text=${text}`, '_blank');
                                        }}
                                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-emerald-600/20">
                                        <MessageSquare size={16} /> Share via WhatsApp
                                    </button>

                                    <button
                                        onClick={() => setCreatedCredentials(null)}
                                        className="w-full py-2.5 text-slate-500 font-semibold text-xs hover:text-slate-700 transition-colors">
                                        Done & View Organizations
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}